import React, { useState, useEffect } from 'react';
import { userRolesAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Shield, 
  ShieldCheck, 
  ShieldAlert, 
  Users, 
  Briefcase,
  Clock,
  Receipt,
  BarChart3,
  Loader2,
  RefreshCw,
  Check,
  X,
  Eye
} from 'lucide-react';

const moduleIcons = {
  anagrafica: Users,
  commesse: Briefcase,
  wbs: Briefcase,
  timesheet: Clock,
  costi: Receipt,
  ricavi: Receipt,
  dashboard_report: BarChart3
};

const moduleLabels = {
  anagrafica: 'Anagrafica di Sistema',
  commesse: 'Commesse e Progetti',
  wbs: 'WBS (Fasi e Attività)',
  timesheet: 'Timesheet',
  costi: 'Costi (Esterni e Trasferte)',
  ricavi: 'Ricavi e Fatturazione',
  dashboard_report: 'Dashboard e Report'
};

const getRoleIcon = (codice) => {
  switch (codice) {
    case 'admin': return ShieldCheck;
    case 'coordinator': return Shield;
    case 'pm': return Briefcase;
    case 'risorsa': return Users;
    default: return Shield;
  }
};

const getRoleBadgeVariant = (codice) => {
  switch (codice) {
    case 'admin': return 'default';
    case 'coordinator': return 'secondary';
    case 'pm': return 'outline';
    case 'risorsa': return 'outline';
    default: return 'outline';
  }
};

const PermissionValue = ({ value }) => {
  if (value === true) {
    return <Check className="h-4 w-4 text-emerald-600" />;
  }
  if (value === false) {
    return <X className="h-4 w-4 text-red-500" />;
  }
  if (value === 'readonly' || value === 'limited' || value === 'own_only') {
    return (
      <Badge variant="outline" className="text-xs text-amber-600 border-amber-600">
        <Eye className="h-3 w-3 mr-1" />
        {value === 'readonly' ? 'Sola lettura' : value === 'limited' ? 'Limitato' : 'Solo proprio'}
      </Badge>
    );
  }
  return <span className="text-muted-foreground text-sm">{String(value)}</span>;
};

const PermissionTable = ({ permissions, moduleName }) => {
  if (typeof permissions !== 'object' || permissions === null) {
    return <PermissionValue value={permissions} />;
  }

  // Handle nested objects like 'clienti', 'ruoli', 'risorse' in anagrafica
  const hasNestedObjects = Object.values(permissions).some(
    v => typeof v === 'object' && v !== null && !Array.isArray(v)
  );

  if (hasNestedObjects) {
    return (
      <div className="space-y-4">
        {Object.entries(permissions).map(([key, value]) => {
          if (typeof value === 'object' && value !== null) {
            return (
              <div key={key} className="border rounded-lg p-3 bg-muted/30">
                <h5 className="font-medium text-sm mb-2 capitalize">{key.replace(/_/g, ' ')}</h5>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {Object.entries(value).map(([subKey, subValue]) => (
                    <div key={subKey} className="flex items-center justify-between">
                      <span className="text-muted-foreground capitalize">{subKey.replace(/_/g, ' ')}</span>
                      <PermissionValue value={subValue} />
                    </div>
                  ))}
                </div>
              </div>
            );
          }
          return (
            <div key={key} className="flex items-center justify-between py-1">
              <span className="text-sm capitalize">{key.replace(/_/g, ' ')}</span>
              <PermissionValue value={value} />
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-2">
      {Object.entries(permissions).map(([key, value]) => (
        <div key={key} className="flex items-center justify-between py-1 border-b border-dashed last:border-0">
          <span className="text-sm text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
          <PermissionValue value={value} />
        </div>
      ))}
    </div>
  );
};

export const UserRolesPage = () => {
  const { user } = useAuth();
  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitializing, setIsInitializing] = useState(false);

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      const response = await userRolesAPI.getAll();
      setRoles(response.data);
    } catch (error) {
      console.error('Error loading user roles:', error);
      toast.error('Errore nel caricamento dei ruoli utente');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInitRoles = async () => {
    setIsInitializing(true);
    try {
      await userRolesAPI.initSystemRoles();
      toast.success('Ruoli di sistema inizializzati con successo');
      loadRoles();
    } catch (error) {
      console.error('Error initializing roles:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'inizializzazione dei ruoli');
    } finally {
      setIsInitializing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="user-roles-loading">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-96" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-roles-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Ruoli e Autorizzazioni</h1>
          <p className="text-muted-foreground">Definizione dei ruoli utente e matrice dei permessi</p>
        </div>
        {user?.ruolo === 'admin' && (
          <Button 
            onClick={handleInitRoles} 
            disabled={isInitializing}
            variant="outline"
            data-testid="init-roles-btn"
          >
            {isInitializing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Inizializzazione...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Aggiorna Ruoli DB
              </>
            )}
          </Button>
        )}
      </div>

      {/* Roles Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {roles.map((role) => {
          const RoleIcon = getRoleIcon(role.codice);
          const modules = role.permessi ? 
            Object.entries(role.permessi).filter(([key]) => key !== 'descrizione') : [];
          
          return (
            <Card key={role.codice} className="overflow-hidden" data-testid={`role-card-${role.codice}`}>
              <CardHeader className="border-b bg-muted/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${
                      role.codice === 'admin' ? 'bg-emerald-100 dark:bg-emerald-900' :
                      role.codice === 'coordinator' ? 'bg-blue-100 dark:bg-blue-900' :
                      role.codice === 'pm' ? 'bg-amber-100 dark:bg-amber-900' :
                      'bg-slate-100 dark:bg-slate-800'
                    }`}>
                      <RoleIcon className={`h-5 w-5 ${
                        role.codice === 'admin' ? 'text-emerald-600' :
                        role.codice === 'coordinator' ? 'text-blue-600' :
                        role.codice === 'pm' ? 'text-amber-600' :
                        'text-slate-600'
                      }`} />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{role.nome}</CardTitle>
                      <Badge variant={getRoleBadgeVariant(role.codice)} className="mt-1">
                        {role.codice}
                      </Badge>
                    </div>
                  </div>
                </div>
                <CardDescription className="mt-3">
                  {role.descrizione}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <Accordion type="multiple" className="w-full">
                  {modules.map(([moduleName, modulePermissions]) => {
                    const ModuleIcon = moduleIcons[moduleName] || Shield;
                    return (
                      <AccordionItem key={moduleName} value={moduleName}>
                        <AccordionTrigger className="px-4 py-3 hover:bg-muted/50">
                          <div className="flex items-center gap-2">
                            <ModuleIcon className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">
                              {moduleLabels[moduleName] || moduleName.replace(/_/g, ' ')}
                            </span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-4">
                          <PermissionTable 
                            permissions={modulePermissions} 
                            moduleName={moduleName}
                          />
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Legenda */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Legenda</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-6">
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 text-emerald-600" />
              <span className="text-sm">Accesso completo (lettura + scrittura)</span>
            </div>
            <div className="flex items-center gap-2">
              <X className="h-4 w-4 text-red-500" />
              <span className="text-sm">Nessun accesso</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs text-amber-600 border-amber-600">
                <Eye className="h-3 w-3 mr-1" />
                Sola lettura
              </Badge>
              <span className="text-sm">Visualizzazione senza modifica</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs text-amber-600 border-amber-600">
                <Eye className="h-3 w-3 mr-1" />
                Limitato
              </Badge>
              <span className="text-sm">Accesso parziale come specificato</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UserRolesPage;
